/**
 * A message's menu: the entries `menu.ts` lists, as a menu a keyboard can use.
 *
 * It opens from the hover bar's More button or a right-click on the row, and
 * either way lands on the first entry. Up and Down move (and wrap), Home and
 * End jump, Enter and Space are the buttons' own, Escape closes and puts focus
 * back where it came from, and Tab or a click anywhere else closes it.
 *
 * Fixed rather than absolute, because the row it belongs to sits inside a
 * scroller that would clip it; clamped to the window after it has measured
 * itself, because a menu opened on the last message would otherwise hang off
 * the bottom of the screen.
 */

import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { ds } from '../../ds';
import { useT } from '../../i18n';
import type { MenuEntry } from './menu';
import './chat.css';

const EDGE = 8;

type Action = Exclude<MenuEntry['kind'], 'react' | 'separator'>;

const ICONS: Record<Action, string> = {
  reply: 'Reply',
  thread: 'Thread',
  copy: 'Copy',
  translate: 'Translate',
  edit: 'Edit',
  delete: 'Delete'
};

export function MessageMenu({
  entries,
  at,
  onPick,
  onClose
}: {
  entries: MenuEntry[];
  /** Where it opens, in viewport pixels: the pointer, or under the button. */
  at: { x: number; y: number };
  onPick: (entry: MenuEntry) => void;
  onClose: () => void;
}) {
  const { Icon } = ds();
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  // Read while rendering, before the menu takes focus: an effect that read it
  // would, run twice under StrictMode, find the menu's own first entry.
  const opener = useRef(document.activeElement as HTMLElement | null);
  const [place, setPlace] = useState(at);

  // Measured before paint, so the first frame is already inside the window.
  useLayoutEffect(() => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    setPlace({
      x: Math.max(EDGE, Math.min(at.x, window.innerWidth - box.width - EDGE)),
      y: Math.max(EDGE, Math.min(at.y, window.innerHeight - box.height - EDGE))
    });
  }, [at.x, at.y]);

  useEffect(() => {
    items()[0]?.focus();

    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      // Back to whatever opened it, when focus went down with the menu — not
      // when something it opened, the edit dialog, has already taken it.
      const lost = !document.activeElement || document.activeElement === document.body;
      if (lost && opener.current?.isConnected) opener.current.focus();
    };
  }, []);

  const items = () =>
    Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  // Literal keys, so the catalogue test can see every one of them.
  const label = (kind: Action): string => {
    switch (kind) {
      case 'reply':
        return t('conversation.reply');
      case 'thread':
        return t('conversation.thread.reply');
      case 'copy':
        return t('conversation.menu.copy');
      case 'translate':
        return t('conversation.menu.translate');
      case 'edit':
        return t('conversation.menu.edit');
      case 'delete':
        return t('conversation.menu.delete');
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const all = items();
    const index = all.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => {
      e.preventDefault();
      all[(i + all.length) % all.length]?.focus();
    };
    switch (e.key) {
      case 'ArrowDown':
        return go(index + 1);
      case 'ArrowUp':
        return go(index < 0 ? -1 : index - 1);
      case 'ArrowRight':
      case 'ArrowLeft':
        // The reaction row reads left to right; everything else is a column.
        return go(index + (e.key === 'ArrowRight' ? 1 : -1));
      case 'Home':
        return go(0);
      case 'End':
        return go(-1);
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        return onClose();
      case 'Tab':
        return onClose();
    }
  };

  const reactions = entries.filter((e): e is Extract<MenuEntry, { kind: 'react' }> => e.kind === 'react');
  const rest = entries.filter((e) => e.kind !== 'react');

  return (
    <div
      ref={ref}
      role="menu"
      className="rl-menu"
      style={{ left: place.x, top: place.y }}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {reactions.length > 0 && (
        <div className="rl-menu-reactions">
          {reactions.map((entry) => (
            <button
              key={entry.emoji}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="rl-menu-emoji"
              aria-label={`${t('conversation.react')} ${entry.emoji}`}
              onClick={() => onPick(entry)}
            >
              {entry.emoji}
            </button>
          ))}
        </div>
      )}
      {rest.map((entry, i) =>
        entry.kind === 'separator' ? (
          // A separator straight after the reaction row would be a second rule
          // under the row's own.
          i === 0 && reactions.length > 0 ? null : <div key={`s${i}`} role="separator" className="rl-menu-sep" />
        ) : (
          <button
            key={entry.kind}
            type="button"
            role="menuitem"
            tabIndex={-1}
            className="rl-menu-item"
            data-danger={entry.kind === 'delete' || undefined}
            onClick={() => onPick(entry)}
          >
            <Icon name={ICONS[entry.kind]} size={15} />
            <span>{label(entry.kind)}</span>
          </button>
        )
      )}
    </div>
  );
}
